import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useSearchParams } from 'react-router-dom'
import { Plus, Smartphone, Eye, Pencil, Trash2, Monitor, Users, RefreshCw, Tv } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useWatch } from '../../contexts/WatchContext'
import ReactDOM from 'react-dom'
import { canAddDevices, canAssignScreenToDevice, countDevicesRunningScreens, getDeviceLimit } from '@/utils/subscriptionUtils'
import deviceService from '@/utils/services/DeviceService'
import SubscriptionUpgradeModal from '@/components/UI/SubscriptionUpgradeModal'
import DeviceLimitModal from '@/components/UI/DeviceLimitModal'
import { toast } from 'react-hot-toast'
import { useWatchedDeviceService } from '../../hooks/useWatchedDeviceService'
import { useWatchedScreenService } from '../../hooks/useWatchedScreenService'
import channelService from '../../utils/services/ChannelService'
import useGoogleAnalytics from '@/hooks/useGoogleAnalytics'
import SubscriptionService from '@/utils/services/SubscriptionService'
import userService from '@/utils/services/UserService'
import { hasAdminPrivileges, getAdminType } from '@/utils/adminUtils'



// --- Main Page ---
const Devices = () => {
  const { currentUser, currentUserData } = useAuth()
  const { getEffectiveUserId, getEffectiveUser } = useWatch()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { 
    devices, 
    loading, 
    error,
    createCustomToken,
    createDevice,
    updateDevice,
    deleteDevice,
    clearError,
    getUserDevices,
    subscribeToUserDeviceScreenAssignments
  } = useWatchedDeviceService()
  const { 
    screens, 
    getScreen, 
    getDefaultScreen, 
    getScreenName, 
    getScreenThumb, 
    getScreenThumbById 
  } = useWatchedScreenService()
  const [showAdd, setShowAdd] = useState(false)
  const [showEdit, setShowEdit] = useState(null)
  const [showDelete, setShowDelete] = useState(null)
  const [showPreview, setShowPreview] = useState(null)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showDeviceLimitModal, setShowDeviceLimitModal] = useState(false)
  const [recentScreenChanges, setRecentScreenChanges] = useState(new Set())
  const [deviceLimit, setDeviceLimit] = useState(1)
  const [userSubscriptions, setUserSubscriptions] = useState([])
  const [subscriptions, setSubscriptions] = useState([])
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isConnectingDevice, setIsConnectingDevice] = useState(false)
  const [channels, setChannels] = useState([])
  
  // Google Analytics tracking
  const { trackDeviceManagement, trackFeatureUsage, trackError } = useGoogleAnalytics()

  // Handle incoming navigation state to auto-open add device modal
  useEffect(() => {
    if (searchParams.get('openAddDevice')) {
      setShowAdd(true)
      // Clear the state to prevent reopening on subsequent renders
      window.history.replaceState({}, document.title)
    }
  }, [searchParams])

  // Handle AI agent preview requests
  useEffect(() => {
    if (searchParams.get('previewDevice')) {
      const deviceKey = searchParams.get('previewDevice')
      const device = devices.find(d => d.key === deviceKey)
      if (device) {
        setShowPreview(device)
        // Clear the state to prevent reopening on subsequent renders
        window.history.replaceState({}, document.title)
      }
    }
  }, [searchParams, devices])

  // Handle AI agent edit device requests
  useEffect(() => {
    if (searchParams.get('editDevice')) {
      const deviceKey = searchParams.get('editDevice')
      const device = devices.find(d => d.key === deviceKey)
      if (device) {
        setShowEdit(device)
        // Clear the state to prevent reopening on subsequent renders
        window.history.replaceState({}, document.title)
      }
    }
  }, [searchParams, devices])

  // Handle AI agent delete device requests
  useEffect(() => {
    if (searchParams.get('deleteDevice')) {
      const deviceKey = searchParams.get('deleteDevice')
      const device = devices.find(d => d.key === deviceKey)
      if (device) {
        setShowDelete(device)
        // Clear the state to prevent reopening on subsequent renders
        window.history.replaceState({}, document.title)
      }
    }
  }, [searchParams, devices])

  // Listen for AI agent refresh events
  useEffect(() => {
    const handleDevicesRefresh = (event) => {
      // Force refresh of devices data
      const fetchDevices = async () => {
        try {
          await getUserDevices(false) // Force fresh data
        } catch (error) {
          console.error('❌ Error refreshing devices after AI agent deletion:', error)
        }
      }
      fetchDevices()
    }

    // Add event listener
    window.addEventListener('devices-refresh', handleDevicesRefresh)

    // Cleanup
    return () => {
      window.removeEventListener('devices-refresh', handleDevicesRefresh)
    }
  }, [getUserDevices])

  // Load subscription data and calculate device limit
  useEffect(() => {
    const loadSubscriptionData = async () => {
      try {
        // Load subscriptions and user subscriptions
        const [subscriptionsData, userSubs] = await Promise.all([
          SubscriptionService.loadSubscriptions(true),
          userService.getUserSubscriptions(currentUser.uid, true)
        ])
        
        setSubscriptions(subscriptionsData)
        setUserSubscriptions(userSubs)
        
        // Calculate device limit
        const limit = SubscriptionService.getDeviceLimit(currentUserData, userSubs, subscriptionsData)
        setDeviceLimit(limit)
      } catch (error) {
        console.error('Error loading subscription data:', error)
      }
    }

    if (currentUser && currentUserData) {
      loadSubscriptionData()
    }
  }, [currentUser, currentUserData])

  // Load channels data
  useEffect(() => {
    const loadChannels = async () => {
      try {
        const channelsData = await channelService.getAllChannels()
        setChannels(channelsData)
      } catch (error) {
        console.error('Error loading channels:', error)
      }
    }

    loadChannels()
  }, [])

  // Helper functions for channel data
  const getChannel = (channelId) => {
    return channels.find(c => c.key === channelId)
  }

  const getChannelName = (channelId) => {
    const channel = getChannel(channelId)
    return channel ? channel.name : 'Unknown Channel'
  }

  const getChannelThumb = (channelId) => {
    const channel = getChannel(channelId)
    return channel ? channel.thumbnail : null
  }

  // Manual refresh function
  const handleManualRefresh = async () => {
    setIsRefreshing(true)
    try {
      await getUserDevices(false) // Force fresh data
      toast.success('Devices refreshed successfully!')
    } catch (error) {
      console.error('Error refreshing devices:', error)
      toast.error('Failed to refresh devices')
    } finally {
      setIsRefreshing(false)
    }
  }

  // Enhanced real-time update handling
  useEffect(() => {
    const handleDevicesDataRefresh = (event) => {
      console.log('🔄 Devices page - Received refresh event:', event.detail)
      // Force refresh of devices data
      const fetchDevices = async () => {
        try {
          await getUserDevices(false) // Force fresh data
          console.log('✅ Devices refreshed after event')
        } catch (error) {
          console.error('❌ Error refreshing devices after GlobalDeviceMonitor event:', error)
        }
      }
      fetchDevices()
    }

    // Add event listener
    window.addEventListener('devices-data-refreshed', handleDevicesDataRefresh)

    // Cleanup
    return () => {
      window.removeEventListener('devices-data-refreshed', handleDevicesDataRefresh)
    }
  }, [getUserDevices])


  // Listen for screen assignment changes
  useEffect(() => {
    if (!currentUser?.uid) return

    const unsubscribe = subscribeToUserDeviceScreenAssignments((updatedDevices, changes) => {
      console.log('🔄 Screen assignment changes detected:', changes)
      
      // Show notification for screen assignment changes and track recent changes
      changes.forEach(change => {
        const device = updatedDevices.find(d => d.key === change.newDoc.key)
        if (device) {
          const oldScreenName = change.oldDoc.screenId ? 'Previous Screen' : 'No Screen'
          const newScreenName = change.newDoc.screenId ? 'New Screen' : 'No Screen'
          
          // Add device to recent changes set
          setRecentScreenChanges(prev => new Set([...prev, device.key]))
          
          // Remove from recent changes after 5 seconds
          setTimeout(() => {
            setRecentScreenChanges(prev => {
              const newSet = new Set(prev)
              newSet.delete(device.key)
              return newSet
            })
          }, 5000)
          
          toast.success(
            `Screen assignment updated for ${device.name}: ${oldScreenName} → ${newScreenName}`,
            { duration: 4000 }
          )
        }
      })
    })

    return () => {
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [currentUser?.uid, subscribeToUserDeviceScreenAssignments])

  // Helper: is device online? (now handled by service layer)
  const isOnline = (device) => device.isOnline

  // Add Device
  const handleAdd = async (code) => {
    setIsConnectingDevice(true)
    try {
      console.log('🔗 Starting device connection process...')
      
      // Create custom token for device authentication
      console.log('🔑 Creating custom token for device...')
      await createCustomToken(code)
      console.log('✅ Custom token created successfully')
      
      setShowAdd(false)
      setShowSuccessModal(true)
      
      // Track device addition
      trackDeviceManagement('add_device', 1)
      
      // Show success toast
      toast.success('Device connected successfully! 🎉', {
        duration: 5000,
        icon: '✅'
      })
      
    } catch (error) {
      console.error('❌ Device connection failed:', error)
      
      // Show detailed error message
      let errorMessage = 'Failed to connect device'
      if (error.message) {
        if (error.message.includes('not found')) {
          errorMessage = 'Device code not found. Please check the code and try again.'
        } else if (error.message.includes('already exists')) {
          errorMessage = 'This device is already connected to your account.'
        } else if (error.message.includes('invalid')) {
          errorMessage = 'Invalid device code. Please check the code and try again.'
        } else {
          errorMessage = error.message
        }
      }
      
      toast.error(errorMessage, {
        duration: 6000,
        icon: '❌'
      })
      
      // Track device addition error
      trackError('device_addition_error', error.message)
      
      // Re-throw the error so the modal can handle it
      throw error
    } finally {
      setIsConnectingDevice(false)
    }
  }

  // Edit Device
  const handleEdit = async (device, newName, newScreenId, newChannelId) => {
    try {
      // If assigning a screen, check device limit for screens
      if (newScreenId && newScreenId !== '') {
        const deviceLimitCheck = canAssignScreenToDevice(currentUserData, devices, [], [], device.key)
        
        if (!deviceLimitCheck.canAssign) {
          console.log('Device limit reached for screens:', deviceLimitCheck.reason)
          setShowEdit(null) // Close the current edit modal
          setShowDeviceLimitModal(true)
          return
        }
      }
      
      // Use the same assignment methods as other pages for consistency
      if (newScreenId !== device.screenId) {
        if (newScreenId && newScreenId !== '') {
          // Use assignScreenToDevice for screen assignment (same as Screens page)
          await deviceService.assignScreenToDevice(device.key, newScreenId);
        } else if (device.screenId) {
          // Remove screen assignment using the same service method
          await deviceService.removeScreenFromDevice(device.key);
        }
      }
      
      // Handle channel assignment for channel devices
      if (newChannelId !== device.channelId) {
        if (newChannelId && newChannelId !== '') {
          // Use assignChannelToDevice for channel assignment
          await deviceService.assignChannelToDevice(device.key, newChannelId);
        } else if (device.channelId) {
          // Remove channel assignment using the same service method
          await deviceService.removeChannelFromDevice(device.key);
        }
      }
      
      // Update device name if changed
      if (newName !== device.name) {
        await updateDevice(device.key, { name: newName });
      }
      setShowEdit(null);
      toast.success('Device updated successfully!')
      
      // Track device update
      trackDeviceManagement('update_device', 1)
    } catch (error) {
      toast.error(error.message)
      
      // Track device update error
      trackError('device_update_error', error.message)
    }
  }

  // Delete device
  const handleDelete = async (device) => {
    try {
      await deleteDevice(device.key);
      setShowDelete(null);
      toast.success('Device deleted successfully!')
      
      // Track device deletion
      trackDeviceManagement('delete_device', 1)
    } catch (error) {
      toast.error(error.message)
      
      // Track device deletion error
      trackError('device_deletion_error', error.message)
    }
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          <div className="flex items-center justify-between">
            <span><strong>Error:</strong> {error}</span>
            <button
              onClick={clearError}
              className="text-red-800 hover:text-red-900 font-bold"
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Devices</h1>
          <p className="text-gray-600">Manage your connected digital signage devices</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className={`flex items-center px-3 py-2 rounded-lg transition-colors border border-gray-300 hover:bg-gray-50 ${
              isRefreshing ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            title="Refresh devices"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            className="flex items-center px-4 py-2 rounded-lg transition-colors bg-yellow-500 text-white hover:bg-yellow-600"
            onClick={() => {
              setShowAdd(true)
              trackDeviceManagement('add_device_button_click', 1)
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Device
          </button>
        </div>
      </div>

      {/* Device Limit Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Users className="w-5 h-5 text-blue-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Device Usage</h2>
          {hasAdminPrivileges(getEffectiveUser()) && (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
              {getAdminType(getEffectiveUser())}
            </span>
          )}
        </div>
        
        {hasAdminPrivileges(getEffectiveUser()) ? (
          // Admin/Test/Demo users see unlimited usage
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
              <Users className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">Connected Devices</p>
                <p className="text-lg font-semibold text-green-900">{devices.length}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
              <Monitor className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">Device Limit</p>
                <p className="text-lg font-semibold text-green-900">Unlimited</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
              <Smartphone className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">Available</p>
                <p className="text-lg font-semibold text-green-900">Unlimited</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <Tv className="w-5 h-5 text-orange-600" />
              <div>
                <p className="text-sm text-gray-600">Channel Devices</p>
                <p className="text-lg font-semibold text-orange-900">{devices.filter(device => device.limited).length}</p>
              </div>
            </div>
          </div>
        ) : (
          // Regular users see subscription-based limits
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <Users className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600">Connected Devices</p>
                <p className="text-lg font-semibold text-gray-900">{devices.filter(device => !device.limited).length}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <Monitor className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600">Device Limit</p>
                <p className="text-lg font-semibold text-gray-900">
                  {deviceLimit === Infinity ? 'Unlimited' : deviceLimit}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <Smartphone className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600">Available</p>
                <p className={`text-lg font-semibold ${
                  deviceLimit === Infinity 
                    ? 'text-green-600' 
                    : devices.filter(device => !device.limited).length >= deviceLimit 
                      ? 'text-red-600' 
                      : 'text-green-600'
                }`}>
                  {deviceLimit === Infinity 
                    ? 'Unlimited' 
                    : Math.max(0, deviceLimit - devices.filter(device => !device.limited).length)
                  }
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <Tv className="w-5 h-5 text-orange-600" />
              <div>
                <p className="text-sm text-gray-600">Channel Devices</p>
                <p className="text-lg font-semibold text-orange-900">{devices.filter(device => device.limited).length}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Progress bar for device usage - only show for regular users */}
        {!hasAdminPrivileges(getEffectiveUser()) && deviceLimit !== Infinity && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>Device Usage</span>
              <span>{devices.filter(device => !device.limited).length} / {deviceLimit}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  devices.filter(device => !device.limited).length >= deviceLimit 
                    ? 'bg-red-500' 
                    : devices.filter(device => !device.limited).length >= deviceLimit * 0.8 
                      ? 'bg-yellow-500' 
                      : 'bg-green-500'
                }`}
                style={{ 
                  width: `${Math.min(100, (devices.filter(device => !device.limited).length / deviceLimit) * 100)}%` 
                }}
              ></div>
            </div>
            {devices.filter(device => !device.limited).length >= deviceLimit && (
              <p className="text-sm text-red-600 mt-2">
                You've reached your device limit. Upgrade your plan to add more devices.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        {loading ? (
          <div className="text-center py-12 text-gray-400">
            <div className="flex items-center justify-center mb-4">
              <RefreshCw className="w-6 h-6 animate-spin mr-2" />
              <span>Loading devices...</span>
            </div>
          </div>
        ) : devices.length === 0 ? (
          <div className="text-center py-12">
            <Smartphone className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No devices connected</h3>
            <p className="text-gray-600 mb-6">Connect your first digital signage device to start displaying content</p>
            <button
              className="flex items-center mx-auto px-6 py-3 rounded-lg transition-colors bg-yellow-500 text-white hover:bg-yellow-600"
              onClick={() => {
                setShowAdd(true)
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Connect Device
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Real-time status indicator */}
            <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                <span>Real-time updates active</span>
              </div>
              <div className="text-xs">
                Last updated: {new Date().toLocaleTimeString()}
              </div>
            </div>
            
            {/* Connection in progress indicator */}
            {isConnectingDevice && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
                  <span className="text-blue-800 text-sm font-medium">Connecting new device...</span>
                </div>
              </div>
            )}

            {/* Regular Devices Section */}
            {devices.filter(device => !device.limited).length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Monitor className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Content Devices</h3>
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                    {devices.filter(device => !device.limited).length} device{devices.filter(device => !device.limited).length !== 1 ? 's' : ''}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  These devices can be assigned to custom screens, content, or live channels.
                </p>
                <div className="space-y-4">
                  {devices
                    .filter(device => !device.limited)
                    .sort((a, b) => {
                      // Sort by online status first (online devices first), then by name
                      const aOnline = isOnline(a);
                      const bOnline = isOnline(b);
                      if (aOnline !== bOnline) {
                        return bOnline ? 1 : -1; // Online devices first
                      }
                      return (a.name || '').localeCompare(b.name || '');
                    })
                    .map((device) => (
                    <div 
                      key={device.key} 
                      className={`bg-gray-50 rounded-xl p-6 border transition-all duration-200 ${
                        recentScreenChanges.has(device.key)
                          ? 'border-green-300 bg-green-50 shadow-md'
                          : 'border-gray-100 hover:border-yellow-200 hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        {/* Left side - Device info */}
                        <div className="flex items-center gap-4 flex-1">
                          {/* Status indicator */}
                          <div className="flex flex-col items-center gap-1">
                            {isOnline(device) ? (
                              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                            ) : (
                              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            )}
                            <span className="text-xs text-gray-500">
                              {isOnline(device) ? 'Online' : 'Offline'}
                            </span>
                          </div>

                          {/* Device name and details */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-lg font-semibold text-gray-900">{device.name}</h3>
                              {recentScreenChanges.has(device.key) && (
                                <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  Updated
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <span>v{device.version || '1.0.0'}</span>
                              <span>•</span>
                              <span>{device.manufacturer} {device.model}</span>
                              <span>•</span>
                              <span>
                                {device.lastAccess
                                  ? new Date(device.lastAccess).toLocaleString()
                                  : 'Never'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Right side - Screen/Channel and actions */}
                        <div className="flex items-center gap-4">
                          {/* Screen/Channel preview */}
                          <div className="flex items-center gap-3 bg-white rounded-lg p-3 border border-gray-200">
                            {device.channelId && device.channelId !== '' ? (
                              // Channel preview
                              <>
                                <div className="relative">
                                  <img
                                    src={getChannelThumb(device.channelId) || '/logo.png'}
                                    alt="Channel thumbnail"
                                    className="w-16 h-10 rounded object-cover border border-gray-200"
                                    style={{ aspectRatio: '16/9' }}
                                    onError={(e) => {
                                      console.warn('❌ Failed to load channel thumbnail for device:', device.name, 'channel:', device.channelId)
                                      e.target.style.display = 'none'
                                      e.target.nextSibling.style.display = 'flex'
                                    }}
                                  />
                                  <div 
                                    className="w-16 h-10 bg-gray-200 rounded flex items-center justify-center hidden"
                                    style={{ aspectRatio: '16/9' }}
                                  >
                                    <Tv className="w-4 h-4 text-gray-400" />
                                  </div>
                                  {/* LiveTV indicator */}
                                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                  </div>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {getChannelName(device.channelId)}
                                  </p>
                                  <p className="text-xs text-gray-500 flex items-center gap-1">
                                    <Tv className="w-3 h-3" />
                                    LiveTV
                                  </p>
                                </div>
                              </>
                            ) : getScreenThumbById(device.screenId) || getScreenThumb(getDefaultScreen()) ? (
                              // Screen preview
                              <>
                                <img
                                  src={getScreenThumbById(device.screenId) || getScreenThumb(getDefaultScreen())}
                                  alt="Screen thumbnail"
                                  className="w-16 h-10 rounded object-cover border border-gray-200"
                                  style={{ aspectRatio: '16/9' }}
                                  onError={(e) => {
                                    console.warn('❌ Failed to load thumbnail for device:', device.name, 'screen:', device.screenId)
                                    e.target.style.display = 'none'
                                    e.target.nextSibling.style.display = 'flex'
                                  }}
                                />
                                <div 
                                  className="w-16 h-10 bg-gray-200 rounded flex items-center justify-center hidden"
                                  style={{ aspectRatio: '16/9' }}
                                >
                                  <Monitor className="w-4 h-4 text-gray-400" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {getScreenName(device.screenId) || 'No Default Screen'}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {device.screenId ? 'Assigned Screen' : 'Default Screen'}
                                  </p>
                                </div>
                              </>
                            ) : (
                              // No assignment
                              <div className="flex items-center gap-3">
                                <div className="w-16 h-10 bg-gray-200 rounded flex items-center justify-center">
                                  <Monitor className="w-6 h-6 text-gray-400" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-400">No Assignment</p>
                                  <p className="text-xs text-gray-500">Unassigned</p>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Action buttons */}
                          <div className="flex gap-2">
                            <button
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Preview"
                              onClick={() => setShowPreview(device)}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                              title="Edit"
                              onClick={() => setShowEdit(device)}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                              onClick={() => setShowDelete(device)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Channel Devices Section */}
            {devices.filter(device => device.limited).length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Tv className="w-5 h-5 text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Channel Devices</h3>
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                    {devices.filter(device => device.limited).length} device{devices.filter(device => device.limited).length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Tv className="w-4 h-4 text-orange-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-orange-900 mb-1">Channel-Only Devices</h4>
                      <p className="text-sm text-orange-700">
                        These devices are channel devices that can only display live channel content. They cannot display custom screens, slideshows, or uploaded content. 
                        <br />
                        <strong>Need more devices for your content? Consider upgrading your plan to unlock additional device capacity and expand your digital signage network.</strong>
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  {devices
                    .filter(device => device.limited)
                    .sort((a, b) => {
                      // Sort by online status first (online devices first), then by name
                      const aOnline = isOnline(a);
                      const bOnline = isOnline(b);
                      if (aOnline !== bOnline) {
                        return bOnline ? 1 : -1; // Online devices first
                      }
                      return (a.name || '').localeCompare(b.name || '');
                    })
                    .map((device) => (
                    <div 
                      key={device.key} 
                      className={`bg-orange-50 rounded-xl p-6 border border-orange-200 transition-all duration-200 ${
                        recentScreenChanges.has(device.key)
                          ? 'border-green-300 bg-green-50 shadow-md'
                          : 'hover:border-orange-300 hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        {/* Left side - Device info */}
                        <div className="flex items-center gap-4 flex-1">
                          {/* Status indicator */}
                          <div className="flex flex-col items-center gap-1">
                            {isOnline(device) ? (
                              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                            ) : (
                              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                            )}
                            <span className="text-xs text-gray-500">
                              {isOnline(device) ? 'Online' : 'Offline'}
                            </span>
                          </div>

                          {/* Device name and details */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-lg font-semibold text-gray-900">{device.name}</h3>
                              <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                                <Tv className="w-3 h-3" />
                                Channel
                              </div>
                              {recentScreenChanges.has(device.key) && (
                                <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  Updated
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <span>v{device.version || '1.0.0'}</span>
                              <span>•</span>
                              <span>{device.manufacturer} {device.model}</span>
                              <span>•</span>
                              <span>
                                {device.lastAccess
                                  ? new Date(device.lastAccess).toLocaleString()
                                  : 'Never'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Right side - Channel and actions */}
                        <div className="flex items-center gap-4">
                          {/* Channel preview */}
                          <div className="flex items-center gap-3 bg-white rounded-lg p-3 border border-gray-200">
                            {device.channelId && device.channelId !== '' ? (
                              // Channel preview
                              <>
                                <div className="relative">
                                  <img
                                    src={getChannelThumb(device.channelId) || '/logo.png'}
                                    alt="Channel thumbnail"
                                    className="w-16 h-10 rounded object-cover border border-gray-200"
                                    style={{ aspectRatio: '16/9' }}
                                    onError={(e) => {
                                      console.warn('❌ Failed to load channel thumbnail for device:', device.name, 'channel:', device.channelId)
                                      e.target.style.display = 'none'
                                      e.target.nextSibling.style.display = 'flex'
                                    }}
                                  />
                                  <div 
                                    className="w-16 h-10 bg-gray-200 rounded flex items-center justify-center hidden"
                                    style={{ aspectRatio: '16/9' }}
                                  >
                                    <Tv className="w-4 h-4 text-gray-400" />
                                  </div>
                                  {/* LiveTV indicator */}
                                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                  </div>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {getChannelName(device.channelId)}
                                  </p>
                                  <p className="text-xs text-orange-600 flex items-center gap-1">
                                    <Tv className="w-3 h-3" />
                                    LiveTV Only
                                  </p>
                                </div>
                              </>
                            ) : (
                              // No channel assigned
                              <div className="flex items-center gap-3">
                                <div className="w-16 h-10 bg-orange-100 rounded flex items-center justify-center">
                                  <Tv className="w-6 h-6 text-orange-400" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-orange-600">No Channel</p>
                                  <p className="text-xs text-orange-500">Channel Required</p>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Action buttons */}
                          <div className="flex gap-2">
                            <button
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Preview"
                              onClick={() => setShowPreview(device)}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                              title="Edit"
                              onClick={() => setShowEdit(device)}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                              onClick={() => setShowDelete(device)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Device Modal */}
      {showAdd && (
        <DeviceAddModal
          onClose={() => setShowAdd(false)}
          onAdd={handleAdd}
          isConnecting={isConnectingDevice}
        />
      )}

      {/* Edit Device Modal */}
      {showEdit && (
        <DeviceEditModal
          device={showEdit}
          screens={screens}
          onClose={() => setShowEdit(null)}
          onEdit={handleEdit}
        />
      )}

      {/* Delete Device Modal */}
      {showDelete && (
        <DeviceDeleteModal
          device={showDelete}
          onClose={() => setShowDelete(null)}
          onDelete={handleDelete}
        />
      )}

      {/* Subscription Upgrade Modal */}
      <SubscriptionUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="devices"
      />

      {/* Device Limit Modal */}
      {showDeviceLimitModal && (
        <DeviceLimitModal
          isOpen={showDeviceLimitModal}
          onClose={() => setShowDeviceLimitModal(false)}
          deviceLimit={getDeviceLimit(currentUserData)}
          currentCount={countDevicesRunningScreens(devices)}
        />
      )}

      {/* Success Modal */}
      {showSuccessModal && createPortal(
        <DeviceSuccessModal
          onClose={() => setShowSuccessModal(false)}
        />,
        document.body
      )}

      {/* Preview Device Modal */}
      {showPreview && createPortal(
        <DevicePreviewModal
          device={showPreview}
          screen={getScreen(showPreview.screenId) || getDefaultScreen()}
          isOnline={isOnline}
          onClose={() => setShowPreview(null)}
        />,
        document.body
      )}
    </div>
  )
}

export default Devices

// --- Modal Components ---

function DeviceAddModal({ onClose, onAdd, isConnecting }) {
  const [code, setCode] = useState('')
  const [connectionStep, setConnectionStep] = useState('')
  
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = 'unset' }
  }, [])

  const handleConnect = async () => {
    if (!code.trim()) return
    
    setConnectionStep('Connecting to device...')
    
    try {
      await onAdd(code)
      // Success is handled by the parent component
    } catch (error) {
      // Error is handled by the parent component
      setConnectionStep('')
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && code.trim() && !isConnecting) {
      handleConnect()
    }
  }

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-60 z-[9999] flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg relative">
        <h2 className="text-xl font-bold mb-4">Connect Device</h2>
        
        {/* Connection Progress */}
        {isConnecting && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
              <span className="text-blue-800 text-sm font-medium">{connectionStep}</span>
            </div>
          </div>
        )}
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Device Code
            </label>
            <input
              className={`w-full border rounded-lg p-3 transition-colors ${
                isConnecting 
                  ? 'bg-gray-100 border-gray-300 cursor-not-allowed' 
                  : 'border-gray-300 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500'
              }`}
              placeholder="Enter the device code from your TV"
              value={code}
              onChange={e => !isConnecting && setCode(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isConnecting}
              autoFocus
            />
          </div>
          
          <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
            <div className="flex items-start">
              <div className="text-blue-500 mr-2 mt-0.5">💡</div>
              <div>
                <p className="font-medium mb-1">How to find your device code:</p>
                <ol className="list-decimal list-inside space-y-1 text-xs">
                  <li>On your TV, navigate to the device registration screen</li>
                  <li>Look for the device code displayed on screen</li>
                  <li>Enter that code here to connect your device</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <button 
            className={`px-4 py-2 rounded-lg transition-colors ${
              isConnecting 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
            onClick={onClose}
            disabled={isConnecting}
          >
            Cancel
          </button>
          <button
            className={`px-4 py-2 rounded-lg transition-colors flex items-center ${
              !code.trim() || isConnecting
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-yellow-500 text-white hover:bg-yellow-600'
            }`}
            onClick={handleConnect}
            disabled={!code.trim() || isConnecting}
          >
            {isConnecting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Connecting...
              </>
            ) : (
              'Connect Device'
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

function DeviceEditModal({ device, screens, onClose, onEdit }) {
  const [name, setName] = useState(device.name)
  const [screenId, setScreenId] = useState(device.screenId || '')
  const [channelId, setChannelId] = useState(device.channelId || '')
  const [channels, setChannels] = useState([])
  const [loadingChannels, setLoadingChannels] = useState(false)

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = 'unset' }
  }, [])

          // Load channels if device is a channel device
  useEffect(() => {
    if (device.limited) {
      loadChannels()
    }
  }, [device.limited])

  const loadChannels = async () => {
    try {
      setLoadingChannels(true)
      const channelsData = await channelService.getActiveChannels()
      setChannels(channelsData)
    } catch (error) {
      console.error('Error loading channels:', error)
    } finally {
      setLoadingChannels(false)
    }
  }

  const handleSave = () => {
    // Check if there are any actual changes
    const hasNameChange = name !== device.name
    const hasScreenChange = !device.limited && screenId !== (device.screenId || '')
    const hasChannelChange = device.limited && channelId !== (device.channelId || '')
    
    // Prevent saving if channel device has "NA" selected
    if (device.limited && channelId === 'NA') {
      return // Don't save, user must select a valid channel
    }
    
    // Prevent saving if regular device has "NA" selected for screen
    if (!device.limited && screenId === 'NA') {
      return // Don't save, user must select a valid screen
    }
    
    // Only proceed if there are changes
    if (hasNameChange || hasScreenChange || hasChannelChange) {
      if (device.limited) {
        // For channel devices, only allow channel assignment
        onEdit(device, name, '', channelId)
      } else {
        // For regular devices, allow screen assignment
        onEdit(device, name, screenId, '')
      }
    } else {
      // No changes, just close the modal
      onClose()
    }
  }

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-60 z-[9999] flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg relative">
        <h2 className="text-xl font-bold mb-4">Edit Device</h2>
        
        {device.limited && (
          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-center gap-2 text-orange-800">
              <Tv className="w-4 h-4" />
                                      <span className="text-sm font-medium">Channel Device</span>
            </div>
            <p className="text-xs text-orange-700 mt-1">
              This device can only be assigned to channels, not screens.
            </p>
          </div>
        )}

        <input
          className="w-full border rounded p-2 mb-3"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Device name"
        />

        {device.limited ? (
          // Channel assignment for channel devices
          <div className="mb-3">
            <label className="block mb-1 font-medium">Assigned Channel</label>
            <select
              className="w-full border rounded p-2"
              value={channelId}
              onChange={e => setChannelId(e.target.value)}
              disabled={loadingChannels}
            >
              {loadingChannels ? (
                <option disabled>Loading channels...</option>
              ) : channels.length === 0 ? (
                <option disabled>No channels available</option>
              ) : (
                <>
                  <option value="NA">No Channel Assigned</option>
                  {channels.map(channel => (
                    <option key={channel.key} value={channel.key}>
                      {channel.name}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
        ) : (
          // Screen assignment for regular devices
          <div className="mb-3">
            <label className="block mb-1 font-medium">Assigned Screen</label>
            {screens.length === 0 ? (
              <div className="w-full border rounded p-2 bg-gray-100 text-gray-500">
                No screens available. Create a screen first to assign it to this device.
              </div>
            ) : (
              <select
                className="w-full border rounded p-2"
                value={screenId}
                onChange={e => setScreenId(e.target.value)}
              >
                <option value="NA">No Screen Assigned</option>
                {screens.map(screen => (
                  <option key={screen.key} value={screen.key}>
                    {screen.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button className="px-4 py-2" onClick={onClose}>Cancel</button>
          <button
            className={`px-4 py-2 rounded ${
              !name || (device.limited && channelId === 'NA') || (!device.limited && screenId === 'NA')
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-yellow-500 text-white hover:bg-yellow-600'
            }`}
            onClick={handleSave}
            disabled={!name || (device.limited && channelId === 'NA') || (!device.limited && screenId === 'NA')}
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

function DeviceDeleteModal({ device, onClose, onDelete }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = 'unset' }
  }, [])
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-60 z-[9999] flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg relative">
        <h2 className="text-xl font-bold mb-4">Delete Device</h2>
        <p>Are you sure you want to delete <span className="font-semibold">{device.name}</span>?</p>
        <div className="flex justify-end gap-2 mt-4">
          <button className="px-4 py-2" onClick={onClose}>Cancel</button>
          <button
            className="px-4 py-2 bg-red-500 text-white rounded"
            onClick={() => onDelete(device)}
          >
            Delete
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

function DeviceSuccessModal({ onClose }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = 'unset' }
  }, [])
  
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-60 z-[9999] flex items-center justify-center">
      <div className="bg-white rounded-lg p-8 w-full max-w-md shadow-lg relative text-center">
        <div className="mb-6">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Device connection in progress</h2>
          <p className="text-gray-600">Please continue on the device to finish the connection process.</p>
        </div>
        
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <div className="text-orange-500 mr-3 mt-0.5">📱</div>
            <div className="text-left">
              <p className="text-orange-800 font-medium mb-1">Next steps:</p>
              <ul className="text-orange-700 text-sm space-y-1">
                <li>• Complete the setup on your device</li>
                <li>• Your device will appear in the devices list</li>
                <li>• You can then assign screens and content</li>
              </ul>
            </div>
          </div>
        </div>
        
        <button
          className="w-full px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
          onClick={onClose}
        >
          Continue
        </button>
      </div>
    </div>,
    document.body
  )
}

function DevicePreviewModal({ device, screen, isOnline, onClose }) {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [showDeviceInfo, setShowDeviceInfo] = useState(false)
  const [transition, setTransition] = useState('fadein')
  const [showSlide, setShowSlide] = useState(true)
  const [prevSlideIndex, setPrevSlideIndex] = useState(null)
  const slideInterval = React.useRef(null)

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [])

  // Get thumbnail image/video URL
  const getThumbnailUrl = (screen) => {
    if (!screen) return null
    
    // For video content
    if (screen.videoUrl) {
      return (screen.thumbUniversalUrl || screen.thumbUrl || screen.videoUrl)
        .replace("_video", "_video_thumb")
        .replace('.mp4', '_640x360.jpeg')

    }
    
    // For slides content
    if (screen.slides && screen.slidesData && screen.slidesData[currentSlideIndex]) {
      const slide = screen.slidesData[currentSlideIndex]
      const slideUrl = slide.videoUrl || slide.imageUniversalUrl || slide.imageUrl || slide.thumbUniversalUrl || slide.thumbUrl
      if (slideUrl) {
        return slideUrl
          .replace("_1920x1080.", "_640x360.")
          .replace("_1920x1920.", "_640x640.")
          .replace("_video", "_video_thumb")
          .replace('.mp4', '_640x360.jpeg')
  
      }
    }
    
    // For regular image content
    if (screen.imageUrl) {
      return (screen.imageUniversalUrl || screen.imageUrl)
        .replace("_1920x1080.", "_640x360.")
        .replace("_1920x1920.", "_640x640.")
    }
    
    return null
  }

  // Get thumbnail URL for a specific slide
  const getSlideThumbnailUrl = (slide) => {
    if (!slide) return null
    const slideUrl = slide.videoUrl || slide.imageUniversalUrl || slide.imageUrl || slide.thumbUniversalUrl || slide.thumbUrl
    if (slideUrl) {
      return slideUrl
        .replace("_1920x1080.", "_640x360.")
        .replace("_1920x1920.", "_640x640.")
        .replace("_video", "_video_thumb")
        .replace('.mp4', '_640x360.jpeg')

    }
    return null
  }

  // Get aspect ratio
  const getAspectRatio = (screen) => {
    if (!screen) return '16/9'
    const angle = screen.angle || 0
    return (angle === 90 || angle === 270) ? '9/16' : '16/9'
  }

  // Get current slide's duration and animation
  const getCurrentSlide = () => (screen?.slidesData ? screen.slidesData[currentSlideIndex] : null)
  const getSlideDuration = () => {
    const slide = getCurrentSlide()
    return slide && slide.duration ? slide.duration * 1000 : 2500
  }
  const getSlideAnimation = () => {
    const slide = getCurrentSlide()
    // Map animation field
    if (!slide || !slide.animation) return 'fadein'
    const anim = slide.animation.toLowerCase()
    if (['fadein', 'ltr', 'rtl', 'ttb', 'btt', 'zoomin'].includes(anim)) return anim
    return 'fadein' // default
  }

  // Handle slide navigation
  const nextSlide = () => {
    if (screen?.slidesData && currentSlideIndex < screen.slidesData.length - 1) {
      setTransition(getSlideAnimation())
      setPrevSlideIndex(currentSlideIndex)
      setShowSlide(false)
      setTimeout(() => {
        setCurrentSlideIndex(currentSlideIndex + 1)
        setShowSlide(true)
      }, 300)
    }
  }

  const prevSlide = () => {
    if (currentSlideIndex > 0) {
      setTransition(getSlideAnimation())
      setPrevSlideIndex(currentSlideIndex)
      setShowSlide(false)
      setTimeout(() => {
        setCurrentSlideIndex(currentSlideIndex - 1)
        setShowSlide(true)
      }, 300)
    }
  }

  // Animation classes
  const getTransitionClass = (type, isCurrent) => {
    switch (type) {
      case 'fadein':
        if (!showSlide && isCurrent) return 'absolute inset-0 opacity-0 transition-opacity duration-300 w-full h-full object-contain'
        return isCurrent ? 'absolute inset-0 opacity-100 transition-opacity duration-300 w-full h-full object-contain' : 'absolute inset-0 opacity-100 w-full h-full object-contain'
      case 'ltr':
        if (!showSlide && isCurrent) return 'absolute inset-0 translate-x-full opacity-0 transition-all duration-300 w-full h-full object-contain'
        if (!showSlide && !isCurrent) return 'absolute inset-0 translate-x-0 opacity-100 transition-all duration-300 w-full h-full object-contain'
        return isCurrent ? 'absolute inset-0 translate-x-0 opacity-100 transition-all duration-300 w-full h-full object-contain' : 'absolute inset-0 -translate-x-full opacity-0 transition-all duration-300 w-full h-full object-contain'
      case 'rtl':
        if (!showSlide && isCurrent) return 'absolute inset-0 -translate-x-full opacity-0 transition-all duration-300 w-full h-full object-contain'
        if (!showSlide && !isCurrent) return 'absolute inset-0 translate-x-0 opacity-100 transition-all duration-300 w-full h-full object-contain'
        return isCurrent ? 'absolute inset-0 translate-x-0 opacity-100 transition-all duration-300 w-full h-full object-contain' : 'absolute inset-0 translate-x-full opacity-0 transition-all duration-300 w-full h-full object-contain'
      case 'ttb':
        if (!showSlide && isCurrent) return 'absolute inset-0 translate-y-full opacity-0 transition-all duration-300 w-full h-full object-contain'
        if (!showSlide && !isCurrent) return 'absolute inset-0 translate-y-0 opacity-100 transition-all duration-300 w-full h-full object-contain'
        return isCurrent ? 'absolute inset-0 translate-y-0 opacity-100 transition-all duration-300 w-full h-full object-contain' : 'absolute inset-0 -translate-y-full opacity-0 transition-all duration-300 w-full h-full object-contain'
      case 'btt':
        if (!showSlide && isCurrent) return 'absolute inset-0 -translate-y-full opacity-0 transition-all duration-300 w-full h-full object-contain'
        if (!showSlide && !isCurrent) return 'absolute inset-0 translate-y-0 opacity-100 transition-all duration-300 w-full h-full object-contain'
        return isCurrent ? 'absolute inset-0 translate-y-0 opacity-100 transition-all duration-300 w-full h-full object-contain' : 'absolute inset-0 translate-y-full opacity-0 transition-all duration-300 w-full h-full object-contain'
      case 'zoomin':
        if (!showSlide && isCurrent) return 'absolute inset-0 scale-150 opacity-0 transition-all duration-300 w-full h-full object-contain'
        if (!showSlide && !isCurrent) return 'absolute inset-0 scale-100 opacity-100 transition-all duration-300 w-full h-full object-contain'
        return isCurrent ? 'absolute inset-0 scale-100 opacity-100 transition-all duration-300 w-full h-full object-contain' : 'absolute inset-0 scale-150 opacity-0 transition-all duration-300 w-full h-full object-contain'
      default:
        if (!showSlide && isCurrent) return 'absolute inset-0 opacity-0 transition-opacity duration-300 w-full h-full object-contain'
        if (!showSlide && !isCurrent) return 'absolute inset-0 opacity-100 transition-opacity duration-300 w-full h-full object-contain'
        return 'absolute inset-0 opacity-100 transition-opacity duration-300 w-full h-full object-contain'
    }
  }

  // Initialize transition state when component loads
  React.useEffect(() => {
    if (screen?.slidesData && screen.slidesData.length > 1) {
      setTransition(getSlideAnimation())
    }
  }, [screen])

  // Auto-advance logic with real timing
  React.useEffect(() => {
    if (screen?.slidesData && screen.slidesData.length > 1 && isPlaying) {
      setTransition(getSlideAnimation())
      slideInterval.current = setTimeout(() => {
        setPrevSlideIndex(currentSlideIndex)
        setShowSlide(false)
        setTimeout(() => {
          setCurrentSlideIndex((prev) => (prev + 1) % screen.slidesData.length)
          setShowSlide(true)
        }, 300) // transition duration
      }, getSlideDuration())
    } else {
      clearTimeout(slideInterval.current)
    }
    return () => clearTimeout(slideInterval.current)
  }, [isPlaying, currentSlideIndex, screen])

  const thumbnailUrl = getThumbnailUrl(screen)
  const isVideo = screen?.videoUrl || (screen?.slidesData && screen.slidesData[currentSlideIndex]?.videoUrl)
  const isSlides = screen?.slides && screen.slidesData && screen.slidesData.length > 1

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Monitor className="w-6 h-6 text-yellow-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Screen Preview</h2>
              <p className="text-sm text-gray-600">{screen?.name || 'Unassigned Screen'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isSlides && (
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="px-3 py-1 text-sm bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
              >
                {isPlaying ? 'Pause' : 'Play'} Slides
              </button>
            )}
            <button
              onClick={() => setShowDeviceInfo(!showDeviceInfo)}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Device Info"
            >
              <Smartphone className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex">
          {/* Main preview area */}
          <div className="flex-1 p-6">
            {screen ? (
              <div className="relative">
                {/* Screen content */}
                <div 
                  className="bg-gray-900 rounded-lg overflow-hidden mx-auto"
                  style={{ 
                    maxWidth: '100%',
                    aspectRatio: '16/9',
                    maxHeight: '420px'
                  }}
                >
                  {isVideo ? (
                    <video
                      src={thumbnailUrl}
                      controls
                      autoPlay
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    isSlides ? (
                      <div className="relative w-full h-full">
                        {/* Previous slide for transition */}
                        {prevSlideIndex !== null && prevSlideIndex !== currentSlideIndex && (
                          <img
                            src={getSlideThumbnailUrl(screen.slidesData[prevSlideIndex])}
                            alt="prev"
                            className={getTransitionClass(transition, false)}
                          />
                        )}
                        {/* Current slide */}
                        <img
                          src={getSlideThumbnailUrl(screen.slidesData[currentSlideIndex])}
                          alt={`Screen preview - Slide ${currentSlideIndex + 1}`}
                          className={getTransitionClass(transition, true)}
                        />
                      </div>
                    ) : (
                      <img
                        src={thumbnailUrl}
                        alt="Screen preview"
                        className="w-full h-full object-contain"
                      />
                    )
                  )}
                </div>

                {/* Slide navigation for slides */}
                {isSlides && (
                  <div className="flex items-center justify-center gap-4 mt-4">
                    <button
                      onClick={prevSlide}
                      disabled={currentSlideIndex === 0}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    
                    <div className="flex gap-1">
                      {screen.slidesData.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            if (index !== currentSlideIndex) {
                              setTransition(getSlideAnimation())
                              setPrevSlideIndex(currentSlideIndex)
                              setShowSlide(false)
                              setTimeout(() => {
                                setCurrentSlideIndex(index)
                                setShowSlide(true)
                              }, 300)
                            }
                          }}
                          className={`w-3 h-3 rounded-full transition-all border-2 ${
                            index === currentSlideIndex
                              ? 'bg-yellow-400 border-yellow-400'
                              : 'bg-gray-300 border-gray-300 hover:bg-yellow-200'
                          }`}
                          aria-label={`Go to slide ${index + 1}`}
                        />
                      ))}
                    </div>
                    
                    <button
                      onClick={nextSlide}
                      disabled={currentSlideIndex === screen.slidesData.length - 1}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 bg-gray-100 rounded-lg">
                <div className="text-center">
                  <Monitor className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No screen assigned to this device</p>
                </div>
              </div>
            )}
          </div>

          {/* Device info sidebar */}
          {showDeviceInfo && (
            <div className="w-80 border-l border-gray-200 p-6 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Device Information</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Device Name</label>
                  <p className="text-sm text-gray-900">{device.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Model</label>
                  <p className="text-sm text-gray-900">{device.manufacturer} {device.model}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Version</label>
                  <p className="text-sm text-gray-900">{device.version || '1.0.0'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Last Seen</label>
                  <p className="text-sm text-gray-900">
                    {device.lastAccess ? new Date(device.lastAccess).toLocaleString() : 'Never'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Status</label>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${isOnline(device) ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span className="text-sm text-gray-900">{isOnline(device) ? 'Online' : 'Offline'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
} 